export const TOUR_TRANSLATIONS = {
    en: {
        // Common
        next: "Next",
        back: "Back",
        skip: "Skip",
        finish: "Finish",
        selectLanguage: "Select tour language",

        // AssDetails
        assdetailsFilters: {
            title: "Filters",
            content: "Use these filters to narrow down the data by country, retailer, category, brand, SKU, and date. You can also save your favorite filter combinations for quick access.",
        },
        assdetailsMarketSummary: {
            title: "Market Summary",
            content: "This carousel displays stock status percentages grouped by country. Each card shows the distribution of In Stock, Out of Stock, and Delisted products for a specific market.",
        },
        assdetailsRetailerSummary: {
            title: "Retailer Summary",
            content: "Here you can see the stock status breakdown for each retailer, including the total number of SKUs tracked. This helps identify which retailers have stock issues.",
        },
        assdetailsBrandSummary: {
            title: "Brand Summary",
            content: "This section shows stock status percentages grouped by brand, helping you quickly identify which brands are performing well or facing stock-out issues.",
        },
        assdetailsTable: {
            title: "Stock Details Table",
            content: "The main data table shows detailed stock information for each product, including stock status, days in current status, and last status change dates. Tip: Click on any row to open the stock evolution chart and identify trends over time.",
        },
        assdetailsNotListed: {
            title: "Not Listed Products",
            content: "Click 'Show all assortment' to expand this table, which shows products that are not listed in certain retailers. It provides visibility into product availability gaps across your retail network.",
        },
        assdetailsEvolution: {
            title: "Stock Evolution Chart",
            content: "This chart shows how the stock status has changed over time for the selected product, helping you identify trends and patterns.",
        },
        assdetailsFooter: {
            title: "Color Legend",
            content: "This legend explains the color coding used throughout the dashboard: green for In Stock, red for Out of Stock, and gray for Delisted products.",
        },
        assdetailsCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Assortment
        assortmentFilters: {
            title: "Filters",
            content: "Filter the assortment data by country, retailer, category, brand, SKU, and date. Select specific criteria to focus on the data that matters most to you.",
        },
        assortmentWidgets: {
            title: "Status Widgets",
            content: "These interactive widgets show the overall stock status distribution. Click on any widget to filter the table below by that specific status. Use the 'Show Evolution' option to see how the distribution has changed over time.",
        },
        assortmentTable: {
            title: "Details Table",
            content: "The detailed breakdown of products by their stock status. You can sort and explore the data to identify specific issues. Tip: Click on a row to see the evolution chart for that product.",
        },
        assortmentFooter: {
            title: "Footer Info",
            content: "Additional information and color legend for the status categories used in this dashboard.",
        },

        // Changes
        changesFilters: {
            title: "Filters",
            content: "Filter stock changes by country, retailer, category, brand, SKU, and date range. Use the date pickers to compare changes between two specific dates.",
        },
        changesTable: {
            title: "Changes Table",
            content: "This table shows products that changed their stock status between the selected dates. It displays the previous status, current status, and the date of change. Tip: Click on a row to see the price and stock evolution chart. You can also generate and download detailed reports for further analysis.",
        },
        changesEvolution: {
            title: "Price & Stock Evolution",
            content: "This dual chart shows both price and stock history for the selected product. It helps you understand if price changes correlate with stock status changes.",
        },
        changesCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Share
        shareFilters: {
            title: "Filters",
            content: "Filter the stock share data by country, retailer, category, brand, and SKU. This dashboard shows the distribution of stock statuses across your portfolio.",
        },
        shareTable: {
            title: "Stock Share Table",
            content: "This table displays the percentage distribution of stock statuses (Out of Stock, Delisted, In Stock) across different dimensions. It helps you understand your overall stock health at a glance.",
        },
        shareSkuDetails: {
            title: "SKU Details",
            content: "Expand any row in the table above to see the individual SKU-level details. This lets you drill down from aggregated data to specific product information.",
        },

        // Pricing - Changes
        pricingChangesFilters: {
            title: "Filters",
            content: "Filter price changes by country, retailer, category, brand, SKU, and date range. Compare pricing between two dates to identify which products changed price.",
        },
        pricingChangesWidgets: {
            title: "Price Change Widgets",
            content: "These widgets summarize the price changes by category. They show how many products had price increases, decreases, or remained stable compared to the RRSP.",
        },
        pricingChangesTable: {
            title: "Price Changes Table",
            content: "This table lists all products with price changes between the selected dates. It shows previous price, current price, and the percentage change. Tip: Click on a row to see the historical price evolution chart.",
        },
        pricingChangesEvolution: {
            title: "Price Evolution Chart",
            content: "This chart displays the historical price evolution for the selected product across retailers. Use it to understand pricing trends over time.",
        },
        pricingChangesCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Pricing - Market Analyzer
        marketAnalyzerFilters: {
            title: "Filters",
            content: "Filter market analysis by country, retailer, category, brand, and SKU. Use the products in stock filter to focus on active listings. You can include shipping price in the analysis.",
        },
        marketAnalyzerShareChart: {
            title: "Share Chart",
            content: "This chart shows the distribution of products relative to the recommended price (RRSP). It helps you understand how retailers are positioning their prices in the market.",
        },
        marketAnalyzerTable: {
            title: "Market Analysis Table",
            content: "The main table displays detailed pricing information across retailers, including current price, price vs RRSP, and competitive position. Tip: Click on a row to see the SKU price evolution. Expand any row to see granular details including pricing per retailer.",
        },
        marketAnalyzerEvolution: {
            title: "SKU Price Evolution",
            content: "This chart shows how the selected product's price has evolved across different retailers over time.",
        },
        marketAnalyzerCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Pricing - Price Overview (Discount)
        priceDiscountFilters: {
            title: "Filters",
            content: "Filter the pricing overview by country, retailer, category, brand, and SKU. Toggle shipping price inclusion and filter by stock status for more precise analysis.",
        },
        priceDiscountOffers: {
            title: "Offers Distribution",
            content: "This chart shows the distribution of current offers across your product portfolio, giving you a quick snapshot of pricing positioning.",
        },
        priceDiscountCountryWise: {
            title: "Country-Wise Analysis",
            content: "Compare pricing metrics across different countries. This visualization helps identify regional pricing differences and opportunities.",
        },
        priceDiscountCandlestick: {
            title: "Price Candlestick Chart",
            content: "This candlestick chart shows price ranges (min, max, average) for your products. It helps visualize price dispersion and identify outliers.",
        },
        priceDiscountTopMarket: {
            title: "Top Market Changes",
            content: "See which markets had the biggest price movements. This helps you quickly spot significant pricing shifts that may require attention.",
        },
        priceDiscountTopDispersion: {
            title: "Top Dispersion Changes",
            content: "Identify products with the highest price dispersion across retailers. High dispersion may indicate inconsistent pricing strategies.",
        },

        // Pricing - Price Evolution
        priceEvolutionFilters: {
            title: "Filters",
            content: "Select country, retailer, category, brand, and SKUs to analyze price evolution. You can split the view by retailer to compare pricing across channels.",
        },
        priceEvolutionTabs: {
            title: "View Tabs",
            content: "Switch between different views to analyze price evolution from different perspectives. Each tab provides a unique angle on your pricing data.",
        },
        priceEvolutionWidget: {
            title: "Price Summary",
            content: "A quick summary of key pricing metrics for the selected products, including average price, min/max, and price change percentage.",
        },
        priceEvolutionChart: {
            title: "Price Evolution Chart",
            content: "This chart shows how prices have changed over time for the selected products and retailers. Use it to identify pricing trends and seasonal patterns.",
        },
        priceEvolutionTable: {
            title: "Pricing Table",
            content: "Detailed pricing data in tabular format, allowing you to sort, compare, and analyze prices across different products and retailers.",
        },

        // Pricing - Side Comparison
        sideComparisonFilters: {
            title: "Filters",
            content: "Select two retailers to compare side by side. Choose country, category, brand, and SKUs to narrow down the comparison.",
        },
        sideComparisonTable: {
            title: "Comparison Table",
            content: "This table shows a side-by-side price comparison between the two selected retailers. It highlights price differences, helping you understand competitive positioning.",
        },

        // Product Page - KPIs
        ppKpiFilters: {
            title: "Filters",
            content: "Filter product page KPIs by country, retailer, category, brand, SKU, and SKU group. Use the Split KPI option to break down metrics by different dimensions. Save your favorite filter combinations for quick access.",
        },
        ppKpiTable: {
            title: "KPI Table",
            content: "This table displays key performance indicators for your product pages, such as content scores and compliance metrics. Each KPI column includes links to view detailed data or show its evolution over time. Use the 'Create New Report' button to set up automated KPI reports by email.",
        },
        ppKpiCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Product Page - Details
        ppDetailsFilters: {
            title: "Filters",
            content: "Filter product page details by country, retailer, category, brand, SKU, and SKU group. Use the 'Products In Stock' toggle to focus only on available products.",
        },
        ppDetailsTable: {
            title: "Product Details Table",
            content: "This table shows detailed product page information including content scores, image counts, video availability, ratings, and descriptions. Tip: Click on any row to open the full multimedia detail view with images, videos, ratings, and rich content. Use the 'Create New Report' button for automated reports.",
        },
        ppDetailsModal: {
            title: "Product Detail View",
            content: "Click 'Show details' on any row to open this panel, which shows the complete product page including images, videos, ratings, reviews, description, and rich content. It gives you a full view of how the product appears online.",
        },
        ppDetailsCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Product Page - Image Details
        ppImageDetailsFilters: {
            title: "Filters",
            content: "Filter image details by country, retailer, category, brand, SKU, and SKU group to analyze product image galleries across your portfolio.",
        },
        ppImageDetailsTable: {
            title: "Image Details Table",
            content: "This table shows image analysis for each product, including image match scores and the images in each of the 6 gallery positions. Use 'Show/Hide Master Images' to toggle catalogue reference images. Click on any image column to compare your catalogue images with the retailer's gallery.",
        },
        ppImageDetailsGallery: {
            title: "Image Gallery",
            content: "This view compares your catalogue images with the retailer's gallery images. Verify that the correct images are displayed in each gallery position.",
        },

        // Share of Search - KPIs
        sosKpiFilters: {
            title: "Filters",
            content: "Filter Share of Search KPIs by country, retailer, and keyword. Use the Split KPI option to break down results by different dimensions. Save your favorite filter combinations for quick access.",
        },
        sosKpiTable: {
            title: "KPI Table",
            content: "This table shows keyword performance metrics across countries and retailers. Switch between tabs to view data by Keyword Type, Branded Keywords, or Keyword Categories. Use the action links to drill down or click 'Show KPI Evolution' to see a time-series chart.",
        },
        sosKpiDetails: {
            title: "Keyword Details",
            content: "When you click 'Split by keyword' on the KPI table, this section shows the detailed breakdown per keyword. You can navigate to the Details dashboard or view the KPI evolution chart from here.",
        },

        // Share of Search - Details
        sosDetailsFilters: {
            title: "Filters",
            content: "Filter search results by country, retailer, category, brand, SKU, and keyword. This dashboard shows detailed search result positioning for your products.",
        },
        sosDetailsShareChart: {
            title: "Share Of Brands",
            content: "This chart shows the percentage share of each brand in search results. Click on any brand bar to filter the table below by that brand, letting you focus on specific competitive positions.",
        },
        sosDetailsTable: {
            title: "Search Results Table",
            content: "This table shows detailed search result data including product position, sponsored status, visibility, and tags. Tip: Click on any row to see how its search position and stock status have evolved over time.",
        },
        sosDetailsEvolution: {
            title: "Position & Stock Evolution",
            content: "This chart shows how the product's search position and stock status have evolved over time. It helps identify trends in search visibility.",
        },

        // Share of Search - Status
        sosStatusFilters: {
            title: "Filters",
            content: "Filter keyword status by country, retailer, and keyword. This dashboard provides a quick overview of keyword data availability and quality scores.",
        },
        sosStatusTable: {
            title: "Keyword Status Table",
            content: "This table shows the status of each keyword across retailers, including whether data is available, how many products were found, and the quality score. Tip: Click on a row to see a treemap of score distribution across retailers.",
        },
        sosStatusScoreMap: {
            title: "Score Distribution",
            content: "This treemap shows the score distribution across all retailers for the selected keyword. Larger rectangles indicate higher scores.",
        },

        // Marketplaces - General
        mpGeneralFilters: {
            title: "Filters",
            content: "Filter marketplace data by country, retailer, category, brand, SKU, and more. Toggle 'Products In Stock' to focus on active listings. You can also include shipping price in the analysis and save favorite filter combinations.",
        },
        mpGeneralNumbers: {
            title: "Key Metrics",
            content: "These cards show the main marketplace KPIs at a glance: total number of sellers, products, and offers currently tracked across your selected markets.",
        },
        mpGeneralMarketPosition: {
            title: "Market Position",
            content: "This bubble chart visualizes how products are positioned in the market relative to the recommended price (RRSP). Each bubble represents a product group, with size indicating the number of offers.",
        },
        mpGeneralMarketDetails: {
            title: "Global Market Position Details",
            content: "This table shows detailed market information per retailer, including number of offers, sellers, discount levels, price dispersion, and stock status. Use it to compare marketplace performance across retailers.",
        },
        mpGeneralAuthorized: {
            title: "Authorized Sellers",
            content: "This chart shows the distribution of authorized vs unauthorized sellers across your marketplaces. It helps identify potential unauthorized distribution channels.",
        },
        mpGeneralTopSellers: {
            title: "Top 20 Sellers",
            content: "This table ranks the top 20 sellers by number of products listed. It shows each seller's product count, discount levels, and price dispersion to help identify key marketplace players.",
        },

        // Marketplaces - Lost Buy Box Overview
        lbbOverviewFilters: {
            title: "Filters",
            content: "Filter the Lost Buy Box overview by country, retailer, category, brand, SKU, and SKU group. Save your favorite filter combinations for quick access.",
        },
        lbbOverviewBuyboxShare: {
            title: "Buy Box Share",
            content: "This pie chart shows the proportion of products where you are winning vs losing the Buy Box. A higher winning percentage indicates better marketplace positioning.",
        },
        lbbOverviewReasonShare: {
            title: "Reasons for Losing",
            content: "This chart breaks down the reasons why you are losing the Buy Box (price, shipping, seller rating, etc.). Understanding the reasons helps prioritize corrective actions.",
        },
        lbbOverviewCompetitors: {
            title: "Top 10 Competitors",
            content: "This table shows the top 10 competitors that are winning the Buy Box against you, ranked by number of wins. Use it to identify your main marketplace rivals.",
        },
        lbbOverviewTable: {
            title: "Lost Buy Box Overview Table",
            content: "The main table shows a detailed breakdown per product: Buy Box loss percentage, reasons, average prices, number of sellers, and delivery times. Tip: Click 'Go to details' on any row to see the full Buy Box history for that product.",
        },

        // Marketplaces - Lost Buy Box Details
        lbbDetailsFilters: {
            title: "Filters",
            content: "Filter Lost Buy Box details by country, retailer, category, brand, SKU, SKU group, and loss reason. Use the reason filter to focus on specific types of Buy Box losses.",
        },
        lbbDetailsTable: {
            title: "Lost Buy Box Details Table",
            content: "This table shows every Buy Box loss event with full details: reason, date, retailer, product, price comparison, shipping cost, and delivery time. Tip: Click 'Show Buy Box Evolution' on any row to see the historical Buy Box chart. You can also generate and download reports.",
        },
        lbbDetailsEvolution: {
            title: "Buy Box Evolution Chart",
            content: "This chart shows the Buy Box history for the selected product over time, including which seller held the Buy Box and price changes. It helps identify patterns in Buy Box losses.",
        },
        lbbDetailsCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Marketplaces - Seller Details
        sellerDetailsFilters: {
            title: "Filters",
            content: "Filter seller details by country, retailer, category, brand, SKU, and more. Toggle 'Products In Stock' to focus on active listings. Use the view toggle to switch between seller and product perspectives.",
        },
        sellerDetailsTable: {
            title: "Seller & Product Details Table",
            content: "In seller view, this table shows each seller's performance across retailers: number of offers, products, price dispersion, Buy Box wins, and stock status. Switch to product view to see individual product details with price evolution charts.",
        },

        // Marketplaces - Seller Deep Dive
        sellerDeepDiveFilters: {
            title: "Filters",
            content: "Select a specific seller to analyze in depth. Filter by country, retailer, category, brand, and SKU. Note: exactly one seller must be selected to view this dashboard.",
        },
        sellerDeepDiveChart: {
            title: "RRSP Price Position",
            content: "This chart shows the distribution of the selected seller's prices relative to the recommended price (RRSP): same price, below RRSP, or above RRSP. It helps evaluate the seller's pricing strategy.",
        },
        sellerDeepDiveTable: {
            title: "Seller Deep Dive Table",
            content: "This table shows the selected seller's full product catalog with prices, discounts, competitor counts, shipping costs, and RRSP comparison. Tip: Click on a chart element to see the offer evolution over time. You can also generate reports.",
        },
        sellerDeepDiveCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Marketplaces - Offers Details
        offersDetailsFilters: {
            title: "Filters",
            content: "Filter offers details by country, retailer, category, brand, SKU, and seller. This dashboard shows all marketplace offers with pricing and seller information for each product.",
        },
        offersDetailsTable: {
            title: "Offers Details Table",
            content: "This table lists all marketplace offers per product, showing seller name, price, shipping cost, country availability, and country match status. Use it to monitor third-party offers and competitive pricing across sellers.",
        },

        // Custom Dashboards - Amazon Release Date
        amazonReleaseDateFilters: {
            title: "Filters",
            content: "Filter Amazon release date data by country, site, category, brand, SKU, and SKU group. Save your favorite filter combinations for quick access.",
        },
        amazonReleaseDateTable: {
            title: "Release Date Table",
            content: "This table shows product release date information on Amazon, including scheduled dates, current status, and any discrepancies. Use it to track upcoming launches and ensure dates are correct across marketplaces.",
        },
        amazonReleaseDateCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Custom Dashboards - Embargo
        embargoFilters: {
            title: "Filters",
            content: "Filter embargo data by country, site, category, brand, SKU, and SKU group. Marketplace users can also filter by seller. Use the date range picker to focus on specific embargo periods.",
        },
        embargoTable: {
            title: "Embargo Table",
            content: "This table shows products under embargo with their scheduled release dates, current availability status, and compliance information. Use it to monitor products that should not yet be available for sale and identify any violations.",
        },
        embargoCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Custom Dashboards - Dynamic Assortment
        dynamicAssortmentFilters: {
            title: "Filters",
            content: "Filter dynamic assortment data by country, site, SKU, and SKU group. Save your favorite filter combinations for quick access.",
        },
        dynamicAssortmentTable: {
            title: "Dynamic Assortment Table",
            content: "This table shows the dynamic assortment status for your products across sites, including listing changes and assortment variations over time. Use it to track how your product catalog evolves across retailers.",
        },

        // Custom Dashboards - Amazon PP KPIs
        amazonPPKpisFilters: {
            title: "Filters",
            content: "Filter Amazon product page KPIs by country, site, category, brand, SKU, and SKU group. Use the 'Products In Stock' toggle to focus on available products only.",
        },
        amazonPPKpisTable: {
            title: "Amazon PP KPIs Table",
            content: "This table shows key product page performance metrics for Amazon, including content quality scores, image compliance, and A+ content status. Use it to ensure your Amazon product pages meet quality standards.",
        },

        // Custom Dashboards Primor - Flash Offers
        flashOffersTabs: {
            title: "Retailer Tabs",
            content: "Switch between Druni and Primor tabs to view flash offers for each retailer separately. Each tab shows the specific offers and promotions active for that retailer.",
        },
        flashOffersTable: {
            title: "Flash Offers Table",
            content: "This table shows active flash offers and promotions, including product details, discounted prices, offer duration, and stock availability. Use it to monitor promotional activity and competitive pricing across retailers.",
        },
        flashOffersCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Digital Shelf - General
        dsGeneralFilters: {
            title: "Filters",
            content: "Filter Digital Shelf data by country, retailer, brand, SKU, SKU group, and split dimensions. At least one split KPI must be selected. Save your favorite filter combinations for quick access.",
        },
        dsGeneralTable: {
            title: "Digital Shelf Scores Table",
            content: "This table shows the key Digital Shelf scores: overall Digital Shelf Score, Stock Score, Assortment Score, Product Page Score, and Share of Search Scores (organic and sponsored). Each score includes change indicators. Tip: Click on any score to view its evolution chart. Use the 'Create New Report' button for automated reports.",
        },
        dsGeneralCreateReport: {
            title: "Create Report",
            content: "Use the 'Create New Report' button to generate automated reports with this dashboard's data and have them delivered by email.",
        },

        // Digital Shelf - Share of Browse
        shareBrowseTable: {
            title: "Share of Browse Table",
            content: "This table shows how your products appear across different browse sections (Sections 1-9) for each country and retailer. Click on any section value to drill down into the detailed product positioning within that section.",
        },

        // Digital Shelf - Section Details
        sectionDetailsFilters: {
            title: "Filters",
            content: "Filter section details by country, retailer, category, brand, and SKU. This dashboard shows how your products are positioned within specific browse sections.",
        },
        sectionDetailsTable: {
            title: "Section Analyzer Table",
            content: "This table shows detailed product positioning within the selected browse section, including product image, position number, visibility score, sponsored status, and tags. Use it to understand your shelf placement in each retailer's browse experience.",
        },

        // Catalogue - General
        catalogueGeneralFilters: {
            title: "Filters",
            content: "Use these filters to narrow down the catalogue data by country, retailer, category, and brand. Data in this section is updated every Monday.",
        },
        catalogueGeneralTabs: {
            title: "Brand / Category View",
            content: "Switch between Brand and Category tabs to change how the catalogue data is grouped. Each tab provides a different perspective on your product distribution across retailers.",
        },
        catalogueGeneralNumRetailerChart: {
            title: "Products by Retailer",
            content: "This chart shows the number of products listed per retailer, helping you quickly assess which sites have the broadest catalogue coverage.",
        },
        catalogueGeneralTable: {
            title: "Overview Table",
            content: "The main overview table compares your product portfolio across retailers and sites. Use it to identify coverage gaps and benchmark your presence against the market.",
        },
        catalogueGeneralNumProductsChart: {
            title: "Products by Site",
            content: "This chart displays how your products are distributed across different sites, giving you a visual breakdown of catalogue presence per market.",
        },
        catalogueGeneralPriceChart: {
            title: "Price vs. Product Count",
            content: "This chart correlates pricing distribution with the number of products, helping you understand how price ranges affect catalogue breadth across retailers.",
        },

        // Catalogue - Details
        catalogueDetailsFilters: {
            title: "Filters",
            content: "Filter the detailed catalogue data by country, retailer, category, brand, and SKU. A country selection is required to load data. Data is updated every Monday.",
        },
        catalogueDetailsTable: {
            title: "Details Table",
            content: "This table shows detailed product information for each retailer, including price, stock status, reference codes, and mapping status. Tip: Click on a row to open the price evolution chart. Use the edit icon to map or update product references.",
        },

        // Monitoring - Results
        monitoringResultsFilters: {
            title: "Filters",
            content: "Use the filters to narrow down monitoring results by date, product, metric, market, and retailer. You can save favourite filter combinations for quick access.",
        },
        monitoringResultsTable: {
            title: "Results Table",
            content: "The results table displays monitoring data organised by country tabs. Use the column filters to narrow down by brand, EAN, reference, or stock status. Note: Results are limited to 1,000 rows per country tab.",
        },
        monitoringResultsFooter: {
            title: "Footer Summary",
            content: "The footer provides a summary of the current results set, including counts and relevant statistics for the displayed monitoring data.",
        },
    },
    es: {
        // Common
        next: "Siguiente",
        back: "Anterior",
        skip: "Omitir",
        finish: "Finalizar",
        selectLanguage: "Seleccionar idioma del tour",

        // AssDetails
        assdetailsFilters: {
            title: "Filtros",
            content: "Usa estos filtros para segmentar los datos por país, retailer, categoría, marca, SKU y fecha. También puedes guardar combinaciones de filtros favoritos para acceso rápido.",
        },
        assdetailsMarketSummary: {
            title: "Resumen por Mercado",
            content: "Este carrusel muestra los porcentajes de estado de stock agrupados por país. Cada tarjeta muestra la distribución de productos En Stock, Sin Stock y No Listados para un mercado específico.",
        },
        assdetailsRetailerSummary: {
            title: "Resumen por Retailer",
            content: "Aquí puedes ver el desglose del estado de stock para cada retailer, incluyendo el número total de SKUs monitoreados. Esto ayuda a identificar qué retailers tienen problemas de stock.",
        },
        assdetailsBrandSummary: {
            title: "Resumen por Marca",
            content: "Esta sección muestra los porcentajes de estado de stock agrupados por marca, ayudándote a identificar rápidamente qué marcas están funcionando bien o enfrentan problemas de desabastecimiento.",
        },
        assdetailsTable: {
            title: "Tabla de Detalles de Stock",
            content: "La tabla principal muestra información detallada de stock para cada producto, incluyendo estado de stock, días en el estado actual y fechas del último cambio. Tip: Haz clic en cualquier fila para abrir el gráfico de evolución de stock e identificar tendencias.",
        },
        assdetailsNotListed: {
            title: "Productos No Listados",
            content: "Haz clic en 'Show all assortment' para expandir esta tabla, que muestra los productos que no están listados en ciertos retailers. Proporciona visibilidad sobre las brechas de disponibilidad de productos en tu red de retailers.",
        },
        assdetailsEvolution: {
            title: "Gráfico de Evolución de Stock",
            content: "Este gráfico muestra cómo ha cambiado el estado de stock a lo largo del tiempo para el producto seleccionado, ayudándote a identificar tendencias y patrones.",
        },
        assdetailsFooter: {
            title: "Leyenda de Colores",
            content: "Esta leyenda explica la codificación de colores usada en todo el dashboard: verde para En Stock, rojo para Sin Stock y gris para productos No Listados.",
        },
        assdetailsCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Assortment
        assortmentFilters: {
            title: "Filtros",
            content: "Filtra los datos de surtido por país, retailer, categoría, marca, SKU y fecha. Selecciona criterios específicos para enfocarte en los datos que más te importan.",
        },
        assortmentWidgets: {
            title: "Widgets de Estado",
            content: "Estos widgets interactivos muestran la distribución general del estado de stock. Haz clic en cualquier widget para filtrar la tabla de abajo por ese estado específico. Usa la opción 'Show Evolution' para ver cómo ha cambiado la distribución a lo largo del tiempo.",
        },
        assortmentTable: {
            title: "Tabla de Detalles",
            content: "El desglose detallado de productos por su estado de stock. Puedes ordenar y explorar los datos para identificar problemas específicos. Tip: Haz clic en una fila para ver el gráfico de evolución del producto.",
        },
        assortmentFooter: {
            title: "Información del Pie",
            content: "Información adicional y leyenda de colores para las categorías de estado usadas en este dashboard.",
        },

        // Changes
        changesFilters: {
            title: "Filtros",
            content: "Filtra los cambios de stock por país, retailer, categoría, marca, SKU y rango de fechas. Usa los selectores de fecha para comparar cambios entre dos fechas específicas.",
        },
        changesTable: {
            title: "Tabla de Cambios",
            content: "Esta tabla muestra los productos que cambiaron su estado de stock entre las fechas seleccionadas. Muestra el estado anterior, el estado actual y la fecha del cambio. Tip: Haz clic en una fila para ver el gráfico de evolución de precio y stock. También puedes generar y descargar reportes detallados para análisis adicional.",
        },
        changesEvolution: {
            title: "Evolución de Precio y Stock",
            content: "Este gráfico dual muestra tanto el historial de precios como el de stock para el producto seleccionado. Te ayuda a entender si los cambios de precio se correlacionan con cambios en el estado de stock.",
        },
        changesCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Share
        shareFilters: {
            title: "Filtros",
            content: "Filtra los datos de participación de stock por país, retailer, categoría, marca y SKU. Este dashboard muestra la distribución de estados de stock en tu portafolio.",
        },
        shareTable: {
            title: "Tabla de Participación de Stock",
            content: "Esta tabla muestra la distribución porcentual de los estados de stock (Sin Stock, No Listado, En Stock) a través de diferentes dimensiones. Te ayuda a entender la salud general de tu stock de un vistazo.",
        },
        shareSkuDetails: {
            title: "Detalles por SKU",
            content: "Expande cualquier fila en la tabla de arriba para ver los detalles a nivel de SKU individual. Esto te permite profundizar desde datos agregados hasta información específica de productos.",
        },

        // Pricing - Changes
        pricingChangesFilters: {
            title: "Filtros",
            content: "Filtra los cambios de precio por país, retailer, categoría, marca, SKU y rango de fechas. Compara precios entre dos fechas para identificar qué productos cambiaron de precio.",
        },
        pricingChangesWidgets: {
            title: "Widgets de Cambios de Precio",
            content: "Estos widgets resumen los cambios de precio por categoría. Muestran cuántos productos tuvieron aumentos, disminuciones o se mantuvieron estables respecto al RRSP.",
        },
        pricingChangesTable: {
            title: "Tabla de Cambios de Precio",
            content: "Esta tabla lista todos los productos con cambios de precio entre las fechas seleccionadas. Muestra precio anterior, precio actual y el porcentaje de cambio. Tip: Haz clic en una fila para ver el gráfico de evolución histórica de precio.",
        },
        pricingChangesEvolution: {
            title: "Gráfico de Evolución de Precio",
            content: "Este gráfico muestra la evolución histórica del precio para el producto seleccionado entre retailers. Úsalo para entender las tendencias de precios.",
        },
        pricingChangesCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Pricing - Market Analyzer
        marketAnalyzerFilters: {
            title: "Filtros",
            content: "Filtra el análisis de mercado por país, retailer, categoría, marca y SKU. Usa el filtro de productos en stock para enfocarte en listados activos. Puedes incluir el precio de envío en el análisis.",
        },
        marketAnalyzerShareChart: {
            title: "Gráfico de Participación",
            content: "Este gráfico muestra la distribución de productos respecto al precio recomendado (RRSP). Te ayuda a entender cómo los retailers posicionan sus precios en el mercado.",
        },
        marketAnalyzerTable: {
            title: "Tabla de Análisis de Mercado",
            content: "La tabla principal muestra información detallada de precios entre retailers, incluyendo precio actual, precio vs RRSP y posición competitiva. Tip: Haz clic en una fila para ver la evolución del precio del SKU. Expande cualquier fila para ver detalles granulares incluyendo precios por retailer.",
        },
        marketAnalyzerEvolution: {
            title: "Evolución de Precio por SKU",
            content: "Este gráfico muestra cómo ha evolucionado el precio del producto seleccionado en diferentes retailers a lo largo del tiempo.",
        },
        marketAnalyzerCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Pricing - Price Overview (Discount)
        priceDiscountFilters: {
            title: "Filtros",
            content: "Filtra la vista general de precios por país, retailer, categoría, marca y SKU. Activa la inclusión de precio de envío y filtra por estado de stock para un análisis más preciso.",
        },
        priceDiscountOffers: {
            title: "Distribución de Ofertas",
            content: "Este gráfico muestra la distribución de ofertas actuales en tu portafolio de productos, dándote una vista rápida del posicionamiento de precios.",
        },
        priceDiscountCountryWise: {
            title: "Análisis por País",
            content: "Compara métricas de precios entre diferentes países. Esta visualización ayuda a identificar diferencias de precio regionales y oportunidades.",
        },
        priceDiscountCandlestick: {
            title: "Gráfico de Velas de Precio",
            content: "Este gráfico de velas muestra rangos de precio (mín, máx, promedio) para tus productos. Ayuda a visualizar la dispersión de precios e identificar valores atípicos.",
        },
        priceDiscountTopMarket: {
            title: "Principales Cambios de Mercado",
            content: "Mira qué mercados tuvieron los mayores movimientos de precio. Esto te ayuda a detectar rápidamente cambios de precio significativos que pueden requerir atención.",
        },
        priceDiscountTopDispersion: {
            title: "Principales Cambios de Dispersión",
            content: "Identifica productos con la mayor dispersión de precios entre retailers. Una dispersión alta puede indicar estrategias de precios inconsistentes.",
        },

        // Pricing - Price Evolution
        priceEvolutionFilters: {
            title: "Filtros",
            content: "Selecciona país, retailer, categoría, marca y SKUs para analizar la evolución de precios. Puedes dividir la vista por retailer para comparar precios entre canales.",
        },
        priceEvolutionTabs: {
            title: "Pestañas de Vista",
            content: "Cambia entre diferentes vistas para analizar la evolución de precios desde diferentes perspectivas. Cada pestaña ofrece un ángulo único sobre tus datos de precios.",
        },
        priceEvolutionWidget: {
            title: "Resumen de Precios",
            content: "Un resumen rápido de métricas clave de precios para los productos seleccionados, incluyendo precio promedio, mín/máx y porcentaje de cambio de precio.",
        },
        priceEvolutionChart: {
            title: "Gráfico de Evolución de Precios",
            content: "Este gráfico muestra cómo han cambiado los precios a lo largo del tiempo para los productos y retailers seleccionados. Úsalo para identificar tendencias de precios y patrones estacionales.",
        },
        priceEvolutionTable: {
            title: "Tabla de Precios",
            content: "Datos detallados de precios en formato tabular, permitiéndote ordenar, comparar y analizar precios entre diferentes productos y retailers.",
        },

        // Pricing - Side Comparison
        sideComparisonFilters: {
            title: "Filtros",
            content: "Selecciona dos retailers para compararlos lado a lado. Elige país, categoría, marca y SKUs para acotar la comparación.",
        },
        sideComparisonTable: {
            title: "Tabla de Comparación",
            content: "Esta tabla muestra una comparación de precios lado a lado entre los dos retailers seleccionados. Resalta las diferencias de precio, ayudándote a entender el posicionamiento competitivo.",
        },

        // Product Page - KPIs
        ppKpiFilters: {
            title: "Filtros",
            content: "Filtra los KPIs de product page por país, retailer, categoría, marca, SKU y grupo de SKU. Usa la opción Split KPI para desglosar las métricas por diferentes dimensiones. Guarda tus combinaciones de filtros favoritas para acceso rápido.",
        },
        ppKpiTable: {
            title: "Tabla de KPIs",
            content: "Esta tabla muestra los indicadores clave de rendimiento de tus páginas de producto, como puntuaciones de contenido y métricas de cumplimiento. Cada columna de KPI incluye enlaces para ver datos detallados o mostrar su evolución en el tiempo. Usa el botón 'Create New Report' para configurar reportes automáticos de KPIs por email.",
        },
        ppKpiCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Product Page - Details
        ppDetailsFilters: {
            title: "Filtros",
            content: "Filtra los detalles de product page por país, retailer, categoría, marca, SKU y grupo de SKU. Usa el toggle 'Products In Stock' para enfocarte solo en productos disponibles.",
        },
        ppDetailsTable: {
            title: "Tabla de Detalles de Producto",
            content: "Esta tabla muestra información detallada de las páginas de producto incluyendo puntuaciones de contenido, cantidad de imágenes, disponibilidad de videos, ratings y descripciones. Tip: Haz clic en cualquier fila para abrir la vista multimedia completa con imágenes, videos, ratings y contenido enriquecido. Usa el botón 'Create New Report' para reportes automáticos.",
        },
        ppDetailsModal: {
            title: "Vista de Detalle del Producto",
            content: "Haz clic en 'Show details' en cualquier fila para abrir este panel, que muestra la página completa del producto incluyendo imágenes, videos, ratings, reseñas, descripción y contenido enriquecido. Te da una vista completa de cómo aparece el producto online.",
        },
        ppDetailsCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Product Page - Image Details
        ppImageDetailsFilters: {
            title: "Filtros",
            content: "Filtra los detalles de imágenes por país, retailer, categoría, marca, SKU y grupo de SKU para analizar las galerías de imágenes de productos en tu portafolio.",
        },
        ppImageDetailsTable: {
            title: "Tabla de Detalles de Imágenes",
            content: "Esta tabla muestra el análisis de imágenes para cada producto, incluyendo puntuaciones de coincidencia de imagen y las imágenes en cada una de las 6 posiciones de la galería. Usa 'Show/Hide Master Images' para alternar las imágenes de referencia del catálogo. Haz clic en cualquier columna de imagen para comparar tus imágenes con las de la galería del retailer.",
        },
        ppImageDetailsGallery: {
            title: "Galería de Imágenes",
            content: "Esta vista compara tus imágenes de catálogo con las imágenes de la galería del retailer. Verifica que las imágenes correctas se muestren en cada posición.",
        },

        // Share of Search - KPIs
        sosKpiFilters: {
            title: "Filtros",
            content: "Filtra los KPIs de Share of Search por país, retailer y keyword. Usa la opción Split KPI para desglosar resultados por diferentes dimensiones. Guarda tus combinaciones de filtros favoritas para acceso rápido.",
        },
        sosKpiTable: {
            title: "Tabla de KPIs",
            content: "Esta tabla muestra métricas de rendimiento de keywords por países y retailers. Cambia entre pestañas para ver datos por Tipo de Keyword, Keywords de Marca o Categorías de Keywords. Usa los enlaces de acción para profundizar o haz clic en 'Show KPI Evolution' para ver un gráfico temporal.",
        },
        sosKpiDetails: {
            title: "Detalles de Keywords",
            content: "Al hacer clic en 'Split by keyword' en la tabla de KPIs, esta sección muestra el desglose detallado por keyword. Desde aquí puedes navegar al dashboard de Details o ver el gráfico de evolución del KPI.",
        },

        // Share of Search - Details
        sosDetailsFilters: {
            title: "Filtros",
            content: "Filtra los resultados de búsqueda por país, retailer, categoría, marca, SKU y keyword. Este dashboard muestra el posicionamiento detallado de tus productos en los resultados de búsqueda.",
        },
        sosDetailsShareChart: {
            title: "Share Of Brands",
            content: "Este gráfico muestra el porcentaje de participación de cada marca en los resultados de búsqueda. Haz clic en cualquier barra de marca para filtrar la tabla de abajo por esa marca, permitiéndote enfocarte en posiciones competitivas específicas.",
        },
        sosDetailsTable: {
            title: "Tabla de Resultados de Búsqueda",
            content: "Esta tabla muestra datos detallados de resultados de búsqueda incluyendo posición del producto, estado de patrocinio, visibilidad y etiquetas. Tip: Haz clic en cualquier fila para ver cómo su posición y estado de stock han evolucionado en el tiempo.",
        },
        sosDetailsEvolution: {
            title: "Evolución de Posición y Stock",
            content: "Este gráfico muestra cómo la posición de búsqueda y el estado de stock del producto han evolucionado en el tiempo. Ayuda a identificar tendencias en visibilidad.",
        },

        // Share of Search - Status
        sosStatusFilters: {
            title: "Filtros",
            content: "Filtra el estado de keywords por país, retailer y keyword. Este dashboard proporciona una vista rápida de la disponibilidad de datos y puntuaciones de calidad de keywords.",
        },
        sosStatusTable: {
            title: "Tabla de Estado de Keywords",
            content: "Esta tabla muestra el estado de cada keyword por retailer, incluyendo si hay datos disponibles, cuántos productos se encontraron y la puntuación de calidad. Tip: Haz clic en una fila para ver el treemap de distribución de puntuaciones en todos los retailers.",
        },
        sosStatusScoreMap: {
            title: "Distribución de Puntuaciones",
            content: "Este treemap muestra la distribución de puntuaciones en todos los retailers para el keyword seleccionado. Los rectángulos más grandes indican puntuaciones más altas.",
        },

        // Marketplaces - General
        mpGeneralFilters: {
            title: "Filtros",
            content: "Filtra los datos de marketplace por país, retailer, categoría, marca, SKU y más. Activa 'Products In Stock' para enfocarte en listados activos. También puedes incluir el precio de envío y guardar combinaciones de filtros favoritos.",
        },
        mpGeneralNumbers: {
            title: "Métricas Clave",
            content: "Estas tarjetas muestran los KPIs principales del marketplace de un vistazo: número total de sellers, productos y ofertas actualmente monitoreados en los mercados seleccionados.",
        },
        mpGeneralMarketPosition: {
            title: "Posición de Mercado",
            content: "Este gráfico de burbujas visualiza cómo se posicionan los productos en el mercado respecto al precio recomendado (RRSP). Cada burbuja representa un grupo de productos, con el tamaño indicando el número de ofertas.",
        },
        mpGeneralMarketDetails: {
            title: "Detalles de Posición Global de Mercado",
            content: "Esta tabla muestra información detallada del mercado por retailer, incluyendo número de ofertas, sellers, niveles de descuento, dispersión de precios y estado de stock. Úsala para comparar el rendimiento entre retailers.",
        },
        mpGeneralAuthorized: {
            title: "Sellers Autorizados",
            content: "Este gráfico muestra la distribución de sellers autorizados vs no autorizados en tus marketplaces. Ayuda a identificar posibles canales de distribución no autorizados.",
        },
        mpGeneralTopSellers: {
            title: "Top 20 Sellers",
            content: "Esta tabla clasifica los 20 principales sellers por número de productos listados. Muestra la cantidad de productos, niveles de descuento y dispersión de precios de cada seller para identificar los actores clave del marketplace.",
        },

        // Marketplaces - Lost Buy Box Overview
        lbbOverviewFilters: {
            title: "Filtros",
            content: "Filtra la vista general de Lost Buy Box por país, retailer, categoría, marca, SKU y grupo de SKU. Guarda tus combinaciones de filtros favoritas para acceso rápido.",
        },
        lbbOverviewBuyboxShare: {
            title: "Cuota de Buy Box",
            content: "Este gráfico circular muestra la proporción de productos donde estás ganando vs perdiendo el Buy Box. Un mayor porcentaje de victorias indica mejor posicionamiento en el marketplace.",
        },
        lbbOverviewReasonShare: {
            title: "Razones de Pérdida",
            content: "Este gráfico desglosa las razones por las que estás perdiendo el Buy Box (precio, envío, valoración del seller, etc.). Entender las razones ayuda a priorizar las acciones correctivas.",
        },
        lbbOverviewCompetitors: {
            title: "Top 10 Competidores",
            content: "Esta tabla muestra los 10 principales competidores que te están ganando el Buy Box, ordenados por número de victorias. Úsala para identificar a tus principales rivales en el marketplace.",
        },
        lbbOverviewTable: {
            title: "Tabla General de Lost Buy Box",
            content: "La tabla principal muestra un desglose detallado por producto: porcentaje de pérdida de Buy Box, razones, precios medios, número de sellers y tiempos de entrega. Tip: Haz clic en 'Go to details' en cualquier fila para ver el historial completo del Buy Box de ese producto.",
        },

        // Marketplaces - Lost Buy Box Details
        lbbDetailsFilters: {
            title: "Filtros",
            content: "Filtra los detalles de Lost Buy Box por país, retailer, categoría, marca, SKU, grupo de SKU y razón de pérdida. Usa el filtro de razón para enfocarte en tipos específicos de pérdidas de Buy Box.",
        },
        lbbDetailsTable: {
            title: "Tabla de Detalles de Lost Buy Box",
            content: "Esta tabla muestra cada evento de pérdida de Buy Box con todos los detalles: razón, fecha, retailer, producto, comparación de precios, coste de envío y tiempo de entrega. Tip: Haz clic en 'Show Buy Box Evolution' en cualquier fila para ver el gráfico histórico del Buy Box. También puedes generar y descargar reportes.",
        },
        lbbDetailsEvolution: {
            title: "Gráfico de Evolución del Buy Box",
            content: "Este gráfico muestra el historial del Buy Box para el producto seleccionado a lo largo del tiempo, incluyendo qué seller tenía el Buy Box y los cambios de precio. Ayuda a identificar patrones en las pérdidas de Buy Box.",
        },
        lbbDetailsCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Marketplaces - Seller Details
        sellerDetailsFilters: {
            title: "Filtros",
            content: "Filtra los detalles de sellers por país, retailer, categoría, marca, SKU y más. Activa 'Products In Stock' para enfocarte en listados activos. Usa el toggle de vista para cambiar entre perspectiva de seller y producto.",
        },
        sellerDetailsTable: {
            title: "Tabla de Detalles de Sellers y Productos",
            content: "En la vista de sellers, esta tabla muestra el rendimiento de cada seller por retailer: número de ofertas, productos, dispersión de precios, victorias de Buy Box y estado de stock. Cambia a la vista de productos para ver detalles individuales con gráficos de evolución de precios.",
        },

        // Marketplaces - Seller Deep Dive
        sellerDeepDiveFilters: {
            title: "Filtros",
            content: "Selecciona un seller específico para analizar en profundidad. Filtra por país, retailer, categoría, marca y SKU. Nota: se debe seleccionar exactamente un seller para ver este dashboard.",
        },
        sellerDeepDiveChart: {
            title: "Posición de Precio vs RRSP",
            content: "Este gráfico muestra la distribución de los precios del seller seleccionado respecto al precio recomendado (RRSP): mismo precio, por debajo del RRSP o por encima del RRSP. Ayuda a evaluar la estrategia de precios del seller.",
        },
        sellerDeepDiveTable: {
            title: "Tabla de Análisis de Seller",
            content: "Esta tabla muestra el catálogo completo del seller seleccionado con precios, descuentos, número de competidores, costes de envío y comparación con RRSP. Tip: Haz clic en un elemento del gráfico para ver la evolución de ofertas en el tiempo. También puedes generar reportes.",
        },
        sellerDeepDiveCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Marketplaces - Offers Details
        offersDetailsFilters: {
            title: "Filtros",
            content: "Filtra los detalles de ofertas por país, retailer, categoría, marca, SKU y seller. Este dashboard muestra todas las ofertas del marketplace con información de precios y sellers para cada producto.",
        },
        offersDetailsTable: {
            title: "Tabla de Detalles de Ofertas",
            content: "Esta tabla lista todas las ofertas del marketplace por producto, mostrando nombre del seller, precio, coste de envío, disponibilidad por país y estado de coincidencia de país. Úsala para monitorear ofertas de terceros y precios competitivos entre sellers.",
        },

        // Custom Dashboards - Amazon Release Date
        amazonReleaseDateFilters: {
            title: "Filtros",
            content: "Filtra los datos de fecha de lanzamiento en Amazon por país, sitio, categoría, marca, SKU y grupo de SKU. Guarda tus combinaciones de filtros favoritas para acceso rápido.",
        },
        amazonReleaseDateTable: {
            title: "Tabla de Fechas de Lanzamiento",
            content: "Esta tabla muestra la información de fechas de lanzamiento de productos en Amazon, incluyendo fechas programadas, estado actual y discrepancias. Úsala para seguir los próximos lanzamientos y asegurar que las fechas son correctas en los marketplaces.",
        },
        amazonReleaseDateCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Custom Dashboards - Embargo
        embargoFilters: {
            title: "Filtros",
            content: "Filtra los datos de embargo por país, sitio, categoría, marca, SKU y grupo de SKU. Los usuarios de marketplace también pueden filtrar por seller. Usa el selector de rango de fechas para enfocarte en períodos de embargo específicos.",
        },
        embargoTable: {
            title: "Tabla de Embargos",
            content: "Esta tabla muestra los productos bajo embargo con sus fechas de lanzamiento programadas, estado de disponibilidad actual e información de cumplimiento. Úsala para monitorear productos que aún no deberían estar a la venta e identificar posibles violaciones.",
        },
        embargoCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Custom Dashboards - Dynamic Assortment
        dynamicAssortmentFilters: {
            title: "Filtros",
            content: "Filtra los datos de surtido dinámico por país, sitio, SKU y grupo de SKU. Guarda tus combinaciones de filtros favoritas para acceso rápido.",
        },
        dynamicAssortmentTable: {
            title: "Tabla de Surtido Dinámico",
            content: "Esta tabla muestra el estado del surtido dinámico de tus productos por sitio, incluyendo cambios de listado y variaciones del surtido a lo largo del tiempo. Úsala para seguir cómo evoluciona tu catálogo de productos en los retailers.",
        },

        // Custom Dashboards - Amazon PP KPIs
        amazonPPKpisFilters: {
            title: "Filtros",
            content: "Filtra los KPIs de product page de Amazon por país, sitio, categoría, marca, SKU y grupo de SKU. Usa el toggle 'Products In Stock' para enfocarte solo en productos disponibles.",
        },
        amazonPPKpisTable: {
            title: "Tabla de KPIs de PP Amazon",
            content: "Esta tabla muestra métricas clave de rendimiento de las páginas de producto en Amazon, incluyendo puntuaciones de calidad de contenido, cumplimiento de imágenes y estado del contenido A+. Úsala para asegurar que tus páginas de producto en Amazon cumplen los estándares de calidad.",
        },

        // Custom Dashboards Primor - Flash Offers
        flashOffersTabs: {
            title: "Pestañas de Retailer",
            content: "Cambia entre las pestañas de Druni y Primor para ver las ofertas flash de cada retailer por separado. Cada pestaña muestra las ofertas y promociones específicas activas para ese retailer.",
        },
        flashOffersTable: {
            title: "Tabla de Ofertas Flash",
            content: "Esta tabla muestra las ofertas flash y promociones activas, incluyendo detalles del producto, precios con descuento, duración de la oferta y disponibilidad de stock. Úsala para monitorear la actividad promocional y los precios competitivos entre retailers.",
        },
        flashOffersCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Digital Shelf - General
        dsGeneralFilters: {
            title: "Filtros",
            content: "Filtra los datos de Digital Shelf por país, retailer, marca, SKU, grupo de SKU y dimensiones de split. Al menos un split KPI debe estar seleccionado. Guarda tus combinaciones de filtros favoritas para acceso rápido.",
        },
        dsGeneralTable: {
            title: "Tabla de Puntuaciones Digital Shelf",
            content: "Esta tabla muestra las puntuaciones clave del Digital Shelf: Score general, Score de Stock, Score de Surtido, Score de Product Page y Scores de Share of Search (orgánico y patrocinado). Cada puntuación incluye indicadores de cambio. Tip: Haz clic en cualquier puntuación para ver su gráfico de evolución. Usa el botón 'Create New Report' para reportes automáticos.",
        },
        dsGeneralCreateReport: {
            title: "Crear Reporte",
            content: "Usa el botón 'Create New Report' para generar reportes automáticos con los datos de este dashboard y recibirlos por email.",
        },

        // Digital Shelf - Share of Browse
        shareBrowseTable: {
            title: "Tabla de Share of Browse",
            content: "Esta tabla muestra cómo aparecen tus productos en las diferentes secciones de navegación (Secciones 1-9) para cada país y retailer. Haz clic en cualquier valor de sección para profundizar en el posicionamiento detallado del producto dentro de esa sección.",
        },

        // Digital Shelf - Section Details
        sectionDetailsFilters: {
            title: "Filtros",
            content: "Filtra los detalles de sección por país, retailer, categoría, marca y SKU. Este dashboard muestra cómo están posicionados tus productos dentro de secciones específicas de navegación.",
        },
        sectionDetailsTable: {
            title: "Tabla de Análisis de Sección",
            content: "Esta tabla muestra el posicionamiento detallado de productos dentro de la sección de navegación seleccionada, incluyendo imagen del producto, número de posición, puntuación de visibilidad, estado de patrocinio y etiquetas. Úsala para entender tu ubicación en la experiencia de navegación de cada retailer.",
        },

        // Catalogue - General
        catalogueGeneralFilters: {
            title: "Filtros",
            content: "Usa los filtros para acotar los datos del catálogo por país, retailer, categoría y marca. Los datos de esta sección se actualizan todos los lunes.",
        },
        catalogueGeneralTabs: {
            title: "Vista por Marca / Categoría",
            content: "Cambia entre las pestañas de Marca y Categoría para modificar cómo se agrupan los datos del catálogo. Cada pestaña ofrece una perspectiva diferente sobre la distribución de tus productos entre retailers.",
        },
        catalogueGeneralNumRetailerChart: {
            title: "Productos por Retailer",
            content: "Este gráfico muestra el número de productos listados por retailer, ayudándote a identificar rápidamente qué sitios tienen la cobertura de catálogo más amplia.",
        },
        catalogueGeneralTable: {
            title: "Tabla General",
            content: "La tabla principal compara tu cartera de productos entre retailers y sitios. Úsala para identificar huecos de cobertura y comparar tu presencia frente al mercado.",
        },
        catalogueGeneralNumProductsChart: {
            title: "Productos por Sitio",
            content: "Este gráfico muestra cómo están distribuidos tus productos entre los diferentes sitios, ofreciendo un desglose visual de la presencia en el catálogo por mercado.",
        },
        catalogueGeneralPriceChart: {
            title: "Precio vs. Número de Productos",
            content: "Este gráfico correlaciona la distribución de precios con el número de productos, ayudándote a entender cómo los rangos de precio afectan a la amplitud del catálogo entre retailers.",
        },

        // Catalogue - Details
        catalogueDetailsFilters: {
            title: "Filtros",
            content: "Filtra los datos detallados del catálogo por país, retailer, categoría, marca y SKU. Es necesario seleccionar un país para cargar los datos. Los datos se actualizan todos los lunes.",
        },
        catalogueDetailsTable: {
            title: "Tabla de Detalles",
            content: "Esta tabla muestra información detallada de productos por retailer, incluyendo precio, estado de stock, códigos de referencia y estado de mapeo. Tip: Haz clic en una fila para abrir el gráfico de evolución de precios. Usa el icono de edición para mapear o actualizar referencias de productos.",
        },

        // Monitoring - Results
        monitoringResultsFilters: {
            title: "Filtros",
            content: "Usa los filtros para acotar los resultados de monitoring por fecha, producto, métrica, mercado y retailer. Puedes guardar combinaciones de filtros favoritas para acceso rápido.",
        },
        monitoringResultsTable: {
            title: "Tabla de Resultados",
            content: "La tabla muestra los datos de monitoring organizados por pestañas de país. Usa los filtros de columna para acotar resultados por marca, EAN, referencia o estado de stock. Nota: Los resultados están limitados a 1.000 filas por pestaña de país.",
        },
        monitoringResultsFooter: {
            title: "Resumen de Resultados",
            content: "El pie de página proporciona un resumen del conjunto de resultados actual, incluyendo conteos y estadísticas relevantes para los datos de monitoring mostrados.",
        },
    },
}
